import { paddingHorizontal } from '@/features/pos/components/ProductCatalog/parts/CategoriesWithProducts/constants'
import { useCategoryRegistryRef } from '@/features/pos/components/ProductCatalog/parts/CategoryBar/hooks/useCategoryRegistryRef'
import { useCategorySelectionHandlers } from '@/features/pos/components/ProductCatalog/parts/CategoryBar/hooks/useCategorySelectionHandlers'
import BarItem from '@/features/pos/components/ProductCatalog/parts/CategoryBar/parts/BarItem'
import CategoryWithSubcategories from '@/features/pos/components/ProductCatalog/parts/CategoryBar/parts/CategoryWithSubcategories'
import Skeleton from '@/features/pos/components/ProductCatalog/parts/CategoryBar/parts/Skeleton'
import Wrapper from '@/features/pos/components/ProductCatalog/parts/CategoryBar/parts/Wrapper'
import { Category as CategoryData } from '@/features/pos/components/ProductCatalog/parts/CategoryBar/types'
import { FC, useRef } from 'react'
import { ScrollView, View } from 'react-native'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

interface CategoryBarProps {
    categories?: CategoryData[]
    selectedId?: string
    selectedSubcategoryId?: string
    horizontal?: boolean
    onPress: (id?: string, subcategoryId?: string) => void
}

const CategoryBar: FC<CategoryBarProps> = ({
    categories,
    selectedId,
    selectedSubcategoryId,
    horizontal = false,
    onPress,
}) => {
    const containerRef = useRef<View>(null)
    const scrollWrapperRef = useRef<ScrollView>(null)

    const [registryRef, register] = useCategoryRegistryRef()

    const { handleCategoryWithSubcategoriesPress, handleCategoryPress, cancelPendingScroll } =
        useCategorySelectionHandlers(
            { id: selectedId, subcategoryId: selectedSubcategoryId },
            registryRef,
            { containerRef, scrollWrapperRef },
            onPress,
            !horizontal
        )

    const { styles } = useStyles(stylesheet, { horizontal })

    const renderCategories = () => {
        if (!categories) return <Skeleton />

        return categories.map((category, index) => {
            const hasSubcategories = !!category.subcategories?.length

            register(category.id, hasSubcategories, index)

            const selected = category.id === selectedId

            if (hasSubcategories) {
                return (
                    <CategoryWithSubcategories
                        key={category.id}
                        selected={selected}
                        id={category.id}
                        name={category.name}
                        horizontal={horizontal}
                        subcategories={category.subcategories!}
                        selectedSubcategoryId={selected ? selectedSubcategoryId : undefined}
                        onPress={handleCategoryWithSubcategoriesPress}
                    />
                )
            }

            return (
                <BarItem
                    key={category.id}
                    selected={selected}
                    id={category.id}
                    onPress={handleCategoryPress}
                >
                    {category.name}
                </BarItem>
            )
        })
    }

    return (
        <View style={styles.container}>
            <Wrapper
                horizontal={horizontal}
                ref={scrollWrapperRef}
                contentRef={containerRef}
                onScrollBeginDrag={cancelPendingScroll}
            >
                {renderCategories()}
            </Wrapper>
        </View>
    )
}

const stylesheet = createStyleSheet({
    container: {
        variants: {
            horizontal: {
                true: {
                    flexDirection: 'row',
                    alignItems: 'center',
                    paddingLeft: paddingHorizontal,
                },
            },
        },
    },
})

export default CategoryBar
